import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { toast } from 'sonner';
import { getAllJobsForAdmin, getJobStatistics, resetJobToPending } from '@/services/jobService';
import { CheckCircle, XCircle, Building2, Calendar, Info, RotateCcw, Bot, Search, CalendarDays, CalendarClock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AIResultDetailModal } from '@/components/jobs/AIResultDetailModal';
import { Pagination } from '@/components/common/Pagination';
import ConfirmationDialog from '@/components/common/ConfirmationDialog';

export function AIModeratedJobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedJob, setSelectedJob] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [stats, setStats] = useState({ active: 0, inactive: 0 });
  const [dateFilter, setDateFilter] = useState('all');
  const [customDate, setCustomDate] = useState('');
  const [showCustomDateInput, setShowCustomDateInput] = useState(false);
  const [undoConfirm, setUndoConfirm] = useState({
    open: false,
    jobIds: [],
    fromDetail: false,
  });
  const [meta, setMeta] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    limit: 10
  });

  // Calculate date ranges
  const getDateRange = (filter) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (filter) {
      case 'today':
        return { from: today, to: new Date() };
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return { from: yesterday, to: today };
      case 'last7days':
        const last7 = new Date(today);
        last7.setDate(last7.getDate() - 7);
        return { from: last7, to: new Date() };
      case 'last30days':
        const last30 = new Date(today);
        last30.setDate(last30.getDate() - 30);
        return { from: last30, to: new Date() };
      default:
        return null;
    }
  };

  // Filter jobs by date
  const filterJobsByDate = (jobsList) => {
    if (dateFilter === 'all') return jobsList;
    
    // Custom date filter
    if (dateFilter === 'custom' && customDate) {
      const selectedDate = new Date(customDate);
      const startOfDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
      const endOfDay = new Date(startOfDay);
      endOfDay.setDate(endOfDay.getDate() + 1);
      
      return jobsList.filter(job => {
        const moderatedDate = new Date(job.aiModerationResult?.moderatedAt);
        return moderatedDate >= startOfDay && moderatedDate < endOfDay;
      });
    }
    
    const range = getDateRange(dateFilter);
    if (!range) return jobsList;

    return jobsList.filter(job => {
      const moderatedDate = new Date(job.aiModerationResult?.moderatedAt);
      return moderatedDate >= range.from && moderatedDate <= range.to;
    });
  };

  const fetchAIModeratedJobs = useCallback(async (page = meta.currentPage) => {
    try {
      setLoading(true);

      const queryParams = {
        page: page,
        limit: 100, // Get more to filter client-side
        sort: 'createdAt_desc'
      };

      if (searchTerm) queryParams.search = searchTerm;

      // Only filter by moderationStatus, not status
      // This ensures we get all AI-moderated jobs regardless of their current status
      if (statusFilter === 'approved') {
        queryParams.moderationStatus = 'APPROVED';
      } else if (statusFilter === 'rejected') {
        queryParams.moderationStatus = 'REJECTED';
      }

      const response = await getAllJobsForAdmin(queryParams);
      
      const allJobs = response.data.data || [];
      const aiModeratedJobs = allJobs.filter(
        job => job.aiModerationResult && 
               job.aiModerationResult.moderatedAt &&
               job.aiModerationResult.prediction !== null &&
               job.aiModerationResult.prediction !== undefined
      );

      // Apply date filter
      const filteredJobs = filterJobsByDate(aiModeratedJobs);

      setJobs(filteredJobs);
      setMeta({
        ...response.data.meta,
        totalItems: filteredJobs.length,
        totalPages: Math.ceil(filteredJobs.length / meta.limit)
      });
    } catch (error) {
      console.error('Error fetching AI moderated jobs:', error);
      toast.error('Không thể tải danh sách job đã duyệt bằng AI');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, dateFilter, meta.limit]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await getJobStatistics();
      if (response.data?.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchAIModeratedJobs(1);
    fetchStats();
  }, [searchTerm, statusFilter, dateFilter]);

  const handlePageChange = (newPage) => {
    setMeta((prev) => ({ ...prev, currentPage: newPage }));
    fetchAIModeratedJobs(newPage);
  };

  const handleSearch = () => {
    setSearchTerm(searchInput.trim());
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleBulkUndo = (jobIds) => {
    if (!jobIds?.length) return;
    setUndoConfirm({
      open: true,
      jobIds,
      fromDetail: false,
    });
  };

  const confirmUndo = async () => {
    if (!undoConfirm.jobIds.length) return;
    try {
      setLoading(true);
      let successCount = 0;
      let failCount = 0;

      for (const jobId of undoConfirm.jobIds) {
        try {
          await resetJobToPending(jobId);
          successCount++;
        } catch (error) {
          failCount++;
        }
      }

      if (undoConfirm.jobIds.length === 1) {
        if (successCount === 1) {
          toast.success('Đã hoàn tác: Job được đưa về trạng thái chờ duyệt');
        } else {
          toast.error('Không thể hoàn tác quyết định');
        }
      } else {
        toast.success(`✅ Đã hoàn tác ${successCount} job${failCount > 0 ? `, ${failCount} job lỗi` : ''}`);
      }
      
      // Refresh list
      await fetchAIModeratedJobs();
      if (undoConfirm.fromDetail) {
        setIsDetailModalOpen(false);
        setSelectedJob(null);
      }
      fetchStats();
      setUndoConfirm({ open: false, jobIds: [], fromDetail: false });
    } catch (error) {
      toast.error(undoConfirm.jobIds.length === 1 ? 'Không thể hoàn tác quyết định' : 'Có lỗi xảy ra khi hoàn tác');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickUndo = async (filter, customDateValue = null) => {
    let jobsInRange;
    let filterName;

    if (filter === 'custom' && customDateValue) {
      const selectedDate = new Date(customDateValue);
      const startOfDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
      const endOfDay = new Date(startOfDay);
      endOfDay.setDate(endOfDay.getDate() + 1);
      
      jobsInRange = jobs.filter(job => {
        const moderatedDate = new Date(job.aiModerationResult?.moderatedAt);
        return moderatedDate >= startOfDay && moderatedDate < endOfDay;
      });
      
      filterName = `ngày ${selectedDate.toLocaleDateString('vi-VN')}`;
    } else {
      const range = getDateRange(filter);
      if (!range) return;

      jobsInRange = jobs.filter(job => {
        const moderatedDate = new Date(job.aiModerationResult?.moderatedAt);
        return moderatedDate >= range.from && moderatedDate <= range.to;
      });

      const filterNames = {
        today: 'hôm nay',
        yesterday: 'hôm qua',
        last7days: '7 ngày gần nhất',
        last30days: '30 ngày gần nhất'
      };
      filterName = filterNames[filter];
    }

    if (jobsInRange.length === 0) {
      toast.info('Không có job nào trong khoảng thời gian này');
      return;
    }

    handleBulkUndo(jobsInRange.map(j => j._id));
  };

  const handleViewDetail = (job) => {
    const aiResult = job.aiModerationResult;
    setSelectedJob({
      jobId: job._id,
      jobTitle: job.title,
      jobDescription: job.description || '',
      jobRequirements: job.requirements || '',
      success: true,
      approved: job.moderationStatus === 'APPROVED',
      confidence: aiResult.confidence,
      probabilities: aiResult.probabilities || {
        approve: aiResult.confidence,
        reject: 1 - aiResult.confidence
      },
      reasons: aiResult.reasons || [],
      summary: aiResult.summary || '',
      method: aiResult.method || 'PhoBERT'
    });
    setIsDetailModalOpen(true);
  };

  const handleUndoDecision = (jobId) => {
    setUndoConfirm({
      open: true,
      jobIds: [jobId],
      fromDetail: true,
    });
  };

  const getStatusBadge = (job) => {
    if (job.moderationStatus === 'APPROVED') {
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200">
          <CheckCircle className="w-3 h-3 mr-1" />
          Đã duyệt
        </Badge>
      );
    }
    if (job.moderationStatus === 'REJECTED') {
      return (
        <Badge variant="destructive">
          <XCircle className="w-3 h-3 mr-1" />
          Đã từ chối
        </Badge>
      );
    }
    return <Badge variant="outline">Không xác định</Badge>;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
          <Bot className="w-8 h-8 text-purple-600" />
          Job Đã Duyệt Bằng AI
        </h1>
        <p className="text-gray-600 mt-2">Xem lại các job đã được AI phân tích và duyệt</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-700">Tổng số job AI duyệt</p>
                <p className="text-3xl font-bold text-blue-900 mt-2">
                  {(stats.aiApproved || 0) + (stats.aiRejected || 0)}
                </p>
              </div>
              <Bot className="w-12 h-12 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-700">Đã phê duyệt</p>
                <p className="text-3xl font-bold text-green-900 mt-2">
                  {stats.aiApproved || 0}
                </p>
              </div>
              <CheckCircle className="w-12 h-12 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-700">Đã từ chối</p>
                <p className="text-3xl font-bold text-red-900 mt-2">
                  {stats.aiRejected || 0}
                </p>
              </div>
              <XCircle className="w-12 h-12 text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Danh Sách Job</CardTitle>
          <CardDescription>Tất cả job đã được AI phân tích và đưa ra quyết định</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="md:col-span-2 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Tìm kiếm theo tiêu đề..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                className="pl-10 pr-20"
              />
              <div className="absolute right-2 top-2 flex gap-1">
                {searchInput && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleClearSearch}
                    className="h-6 w-6 p-0"
                  >
                    <XCircle className="w-3 h-3" />
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={handleSearch}
                  disabled={loading}
                  className="h-6 px-2 text-xs"
                >
                  Tìm
                </Button>
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Lọc theo kết quả" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tất cả</SelectItem>
                <SelectItem value="approved">Đã phê duyệt</SelectItem>
                <SelectItem value="rejected">Đã từ chối</SelectItem>
              </SelectContent>
            </Select>
            <Select value={dateFilter} onValueChange={(value) => {
              setDateFilter(value);
              setShowCustomDateInput(value === 'custom');
            }}>
              <SelectTrigger>
                <SelectValue placeholder="Lọc theo thời gian" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tất cả thời gian</SelectItem>
                <SelectItem value="today">Hôm nay</SelectItem>
                <SelectItem value="yesterday">Hôm qua</SelectItem>
                <SelectItem value="last7days">7 ngày gần nhất</SelectItem>
                <SelectItem value="last30days">30 ngày gần nhất</SelectItem>
                <SelectItem value="custom">Chọn ngày cụ thể...</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Custom Date Picker */}
          {showCustomDateInput && (
            <div className="flex items-center gap-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <CalendarClock className="w-5 h-5 text-blue-600" />
              <Input
                type="date"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
                className="flex-1"
                max={new Date().toISOString().split('T')[0]}
              />
              {customDate && (
                <Button
                  size="sm"
                  onClick={() => {
                    setCustomDate('');
                    setDateFilter('all');
                    setShowCustomDateInput(false);
                  }}
                  variant="outline"
                >
                  Xóa
                </Button>
              )}
            </div>
          )}

          {/* Quick Undo Actions */}
          <div className="flex gap-2 mb-6 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleQuickUndo('today')}
              disabled={loading}
              className="text-orange-600 border-orange-300 hover:bg-orange-50"
            >
              <CalendarDays className="w-4 h-4 mr-1" />
              Hoàn tác hôm nay
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleQuickUndo('last7days')}
              disabled={loading}
              className="text-orange-600 border-orange-300 hover:bg-orange-50"
            >
              <CalendarDays className="w-4 h-4 mr-1" />
              Hoàn tác 7 ngày
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleQuickUndo('last30days')}
              disabled={loading}
              className="text-orange-600 border-orange-300 hover:bg-orange-50"
            >
              <CalendarDays className="w-4 h-4 mr-1" />
              Hoàn tác 30 ngày
            </Button>
            {customDate && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleQuickUndo('custom', customDate)}
                disabled={loading}
                className="text-orange-600 border-orange-300 hover:bg-orange-50"
              >
                <CalendarClock className="w-4 h-4 mr-1" />
                Hoàn tác ngày {new Date(customDate).toLocaleDateString('vi-VN')}
              </Button>
            )}
          </div>

          {/* Job List */}
          {loading ? (
            <div className="text-center py-8 text-gray-500">Đang tải...</div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-8">
              <Bot className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">Chưa có job nào được AI duyệt</p>
            </div>
          ) : (
            <div className="space-y-4">
              {jobs.map((job) => (
                <Card key={job._id} className="border border-gray-200 hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-6">
                      <div className="flex items-start gap-4 flex-1">
                        <div className="w-14 h-14 bg-purple-100 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                          {job.recruiterProfileId?.company?.logo ? (
                            <img
                              src={job.recruiterProfileId.company.logo}
                              alt="Company"
                              className="w-full h-full object-cover rounded-lg"
                            />
                          ) : (
                            <Building2 className="w-7 h-7 text-purple-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-semibold">{job.title}</h3>
                            {getStatusBadge(job)}
                            <Badge variant="outline" className="text-xs">
                              <Bot className="w-3 h-3 mr-1" />
                              {job.aiModerationResult?.method || 'AI'}
                            </Badge>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Building2 className="w-4 h-4" />
                              <span>{job.recruiterProfileId?.company?.name || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                <span>Duyệt: {new Date(job.aiModerationResult.moderatedAt).toLocaleDateString('vi-VN')}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Info className="w-3 h-3" />
                                <span>Độ tin cậy: {(job.aiModerationResult.confidence * 100).toFixed(1)}%</span>
                              </div>
                            </div>
                            {job.aiModerationResult.summary && (
                              <p className="text-sm text-gray-600 italic line-clamp-2">
                                "{job.aiModerationResult.summary}"
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewDetail(job)}
                        >
                          <Info className="w-4 h-4 mr-1" />
                          Chi tiết
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUndoDecision(job._id)}
                          disabled={loading}
                          className="text-orange-600 border-orange-300 hover:bg-orange-50"
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Hoàn tác
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <div className="mt-6">
            <Pagination
              currentPage={meta.currentPage}
              totalPages={meta.totalPages}
              onPageChange={handlePageChange}
              loading={loading}
            />
          </div>
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <ConfirmationDialog
        open={undoConfirm.open}
        onOpenChange={(open) => {
          if (!open && !loading) {
            setUndoConfirm({ open: false, jobIds: [], fromDetail: false });
          }
        }}
        title={undoConfirm.jobIds.length === 1 ? 'Hoàn tác job này?' : `Hoàn tác ${undoConfirm.jobIds.length} job?`}
        description={
          undoConfirm.jobIds.length === 1
            ? 'Job sẽ được đưa về trạng thái chờ duyệt.'
            : 'Các job đã chọn sẽ được đưa về trạng thái chờ duyệt.'
        }
        onConfirm={confirmUndo}
        confirmText="Hoàn tác"
        cancelText="Hủy"
        variant="destructive"
        isLoading={loading}
      />

      <AIResultDetailModal
        result={selectedJob}
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedJob(null);
        }}
        onUndo={handleUndoDecision}
      />
    </div>
  );
}
